import React, { useState } from 'react';
import PropTypes from 'prop-types';
import CanPerform from 'components/shared/CanPerform';
import { FormattedMessage } from 'react-intl';
import { bookingShape, historyShape } from 'constants/propTypes';
import { Spinner } from 'admin-components/components';
import { Row, Column } from 'components/shared/Layout';
import { ButtonWrapper, CtaButton, ModalContents, StyledMissingDetails } from '../AfterSalesCtas.styles';
import getTickets from 'pages/BookingDetails/GenerateTickets/queries/getTickets';
import { useLazyQuery } from '@apollo/client';
import AgentPortalError from 'errors/AgentPortalError';
import Modal from 'components/ui/Modal/Modal';
import { EXCHANGE } from 'constants/afterSalesShortcuts';

import {
  runAfterSaleValidator,
  bookingIsConfirmed,
  getPassengersForItemRefs,
  getUniquePassengersbyId,
  allSelectedProductsConfirmedCheckedInOrPRRRefunded,
  checkPaxsSelectionToGeneratePDF,
  containsSNCBItemRefs
} from '../AfterSalesCtas.utils';
import { ConfirmButton } from '../../../../../components/ui/Button';
import { getBookingTravelDocumentsURL } from '../../../../../utils/urls';
import { DETAILS_CAPTURED } from 'constants/booking';

const GenerateTicketsPerPaxCta = ({
  booking,
  selectedItemRefs,
  disabled,
  label,
  isPassportRequired,
  history,
  isThroughFare,
  hasExpiredNonEurostarTickets = false
}) => {
  const membershipNumber = new URLSearchParams(history.location.search).get('membershipNumber');
  const [isPassportOpen, setIsPassportOpen] = useState(false);
  const [isExpiredNonEurostarTicketsSelected, setIsExpiredNonEurostarTicketsSelected] = useState(false);
  const passengers = getPassengersForItemRefs({ selectedItemRefs, allBookingItems: booking.allBookingItems });
  const uniquePassengers = getUniquePassengersbyId({ passengers });

  const isSNCBLegSelected =
    containsSNCBItemRefs(selectedItemRefs, booking.outbound, false) || containsSNCBItemRefs(selectedItemRefs, booking.inbound, false);

  const isDisabled =
    disabled ||
    (isSNCBLegSelected && !isThroughFare) ||
    !runAfterSaleValidator(booking, selectedItemRefs)([bookingIsConfirmed, allSelectedProductsConfirmedCheckedInOrPRRRefunded]) ||
    checkPaxsSelectionToGeneratePDF({ booking, selectedItemRefs, uniquePassengers });
  const [captureModalOpen, setCaptureModalOpen] = useState(false);
  const detailsCaptured = booking?.detailsCaptured === DETAILS_CAPTURED.CAPTURED;

  const handlePDFCompleted = (data) => {
    if (data?.getTickets) {
      let getTickets = data.getTickets;

      const pdfUrls = getTickets.passengers
        ?.map((p) => p.tickets)
        .flat()
        .map((t) => t.ticketUrls.pdfUrl)
        .flat();

      const openedTickets = new Set();

      for (let ticketURL of pdfUrls) {
        if (!openedTickets.has(ticketURL)) {
          openedTickets.add(ticketURL);
          window.open(ticketURL, '_blank');
        }
      }
    }
  };

  const [getTicketsQuery, { loading }] = useLazyQuery(getTickets, {
    fetchPolicy: 'network-only',
    onCompleted: handlePDFCompleted
  });

  const ticketNumbers = booking.inbound
    .concat(booking.outbound)
    .concat(booking.outbound.filter((l) => l.addOnLeg).map((l) => l.addOnLeg))
    .concat(booking.inbound.filter((l) => l.addOnLeg).map((l) => l.addOnLeg))
    .flat()
    .map((l) => l.products)
    .flat()
    .filter((p) => p.isCancelled === false)
    .map((p) => ({ ...p, __typename: 'BookingItem' }))
    .filter((p) => selectedItemRefs.includes(p.itemRef))
    .map((p) => p.ticketNumber);

  const handleTicketsGeneration = async () => {
    getTicketsQuery({
      variables: {
        bookingReference: booking.reference,
        lastName: uniquePassengers[0].lastName,
        language: booking.language || 'EN',
        ticketNumbers
      }
    });

    return;
  };

  const handleSubmit = async () => {
    if (isPassportRequired) {
      setIsPassportOpen(true);
      return;
    }

    if (hasExpiredNonEurostarTickets) {
      setIsExpiredNonEurostarTicketsSelected(true);
      return;
    }

    try {
      if (detailsCaptured) {
        handleTicketsGeneration();
        return;
      } else {
        setCaptureModalOpen(true);
        return;
      }
    } catch (error) {
      throw new AgentPortalError(error);
    }
  };

  if (loading) {
    return (
      <Row span={16}>
        <Column justifyContent="center" alignItems="center">
          <Spinner />
        </Column>
      </Row>
    );
  }

  const togglePassportModal = () => {
    setIsPassportOpen(!isPassportOpen);
  };

  const toggleIsNonEurostarTicketsSelected = () => {
    setIsExpiredNonEurostarTicketsSelected(!isExpiredNonEurostarTicketsSelected);
  };

  const onCaptureTravelDocument = () => {
    history.push(getBookingTravelDocumentsURL(booking.reference, membershipNumber));
  };

  return (
    <div data-testId="generate-pdf-tickets">
      <Modal onClose={() => setCaptureModalOpen(false)} title={<FormattedMessage id="issueTickets.title" />} isOpen={captureModalOpen}>
        <StyledMissingDetails data-cy="missing-details">
          <FormattedMessage id="ticketMethods.missingDetails" />
        </StyledMissingDetails>
      </Modal>

      <Modal isOpen={isPassportOpen} title="Error" onClose={togglePassportModal}>
        <ModalContents>
          <FormattedMessage id="issueTicket.passportRequired1" />
          <br />
          <FormattedMessage id="issueTicket.passportRequired2" />
        </ModalContents>
        <ButtonWrapper>
          <ConfirmButton variant="primary" onClick={onCaptureTravelDocument}>
            <FormattedMessage id="advancedPaxInfo.title" />
          </ConfirmButton>
        </ButtonWrapper>
      </Modal>

      <Modal
        isOpen={isExpiredNonEurostarTicketsSelected}
        title={<FormattedMessage id="searchResults.cannotBeBooked" />}
        onClose={toggleIsNonEurostarTicketsSelected}
      >
        <ModalContents>
          <FormattedMessage id={'issueTickets.expiredExternalTickets'} />
        </ModalContents>
      </Modal>

      <CanPerform afterSalesShortcut={EXCHANGE}>
        {(isEligible) =>
          isEligible && <CtaButton onClick={handleSubmit} data-cy="generate-pdf-tickets" label={label} disabled={isDisabled} />
        }
      </CanPerform>
    </div>
  );
};

GenerateTicketsPerPaxCta.propTypes = {
  booking: bookingShape,
  selectedItemRefs: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  label: PropTypes.node,
  isPassportRequired: PropTypes.bool,
  history: historyShape,
  isThroughFare: PropTypes.bool,
  hasExpiredNonEurostarTickets: PropTypes.bool
};

export default GenerateTicketsPerPaxCta;
